import { useState, useRef, useEffect } from 'react';
import type { EnrollmentResponse } from '@exam-prep/contracts';
import QRCode from 'qrcode';
import type { Me } from '../../../lib/auth';
import { post } from '../../../lib/api';
import { ErrorBox } from '../../../components/Feedback';
import { CodeForm } from '../ui/Login';

export function Enrollment({ onDone }: { onDone: (me: Me) => void }) {
  const [data, setData] = useState<EnrollmentResponse | null>(null);
  const [qr, setQr] = useState('');
  const [error, setError] = useState<unknown>(null);
  const started = useRef(false);
  useEffect(() => {
    // Each call issues a new secret: run once even under StrictMode's double effects.
    if (started.current) return;
    started.current = true;
    post<EnrollmentResponse>('/admin/auth/enroll/start')
      .then(async (d) => {
        setData(d);
        setQr(await QRCode.toDataURL(d.otpauthUri, { margin: 1, width: 200 }));
      })
      .catch(setError);
  }, []);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="text-sm text-muted-foreground">Preparing…</p>;
  return (
    <div className="flex flex-col gap-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm">
        <li>Open an authenticator app (Google Authenticator, 1Password, Authy…).</li>
        <li>Scan this QR code, or enter the key manually.</li>
        <li>Type the 6-digit code it shows.</li>
      </ol>
      {qr ? (
        <img
          src={qr}
          alt="TOTP QR code"
          className="mx-auto h-48 w-48 rounded-xl border-4 border-white bg-white shadow-[0_0_0_1px_var(--brand-hairline-strong),0_12px_40px_-16px_var(--brand-glow-amber)]"
        />
      ) : null}
      <code className="rounded-lg border bg-muted p-2 text-center font-mono text-xs tracking-wider break-all">
        {data.secret}
      </code>
      <CodeForm
        cta="Turn on two-factor"
        onSubmit={async (code) => {
          onDone(await post<Me>('/admin/auth/enroll/confirm', { code }));
        }}
      />
    </div>
  );
}
